import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { apiGetDocuments } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, Download, Calendar } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { EmployeeProfile } from "@/data/employees";

interface Props {
  employee: EmployeeProfile;
}

export function ProfileDocumentsTab({ employee }: Props) {
  const { t } = useTranslation();

  const { data: documents = [] } = useQuery({
    queryKey: ["documents", employee.id],
    queryFn: () => apiGetDocuments(employee.id),
  });

  return (
    <div className="max-w-3xl">
      <Card className="shadow-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary" />
            {t("profile.tabs.documents")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {documents.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center italic">
              {t("profile.documents.noDocuments")}
            </p>
          ) : (
            <div className="divide-y divide-border">
              {documents.map((doc) => (
                <div key={doc.id} className="flex items-center gap-4 py-3">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <FileText className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{doc.documentName}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {doc.documentType && (
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                          {doc.documentType}
                        </Badge>
                      )}
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(doc.uploadedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                      </span>
                    </div>
                  </div>
                  {doc.fileUrl && (
                    <button className="h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent transition-colors shrink-0">
                      <Download className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
